import React, { FC, useEffect, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  useTheme,
  useMediaQuery,
  styled,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { FormulaCollapse } from 'src/components/Collapse/FormulaCollapse';
import {
  CompanyForm,
  CompanyFormValues,
} from 'src/components/Forms/CompanyForm';
import {
  ChangePasswordForm,
  ChangePasswordFormValues,
} from 'src/components/Forms/ChangePasswordForm';
import { OperatorIcon } from 'src/components/Forms/CustomFormulaForm/FormulaCalculator/components/OperatorIcon';
import {
  companyFormSchema,
  changePasswordFormSchema,
} from 'src/components/Forms/validationSchemas';
import { DeleteModal } from 'src/components/Modals/Delete';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import {
  AddButton,
  ChipLabelContainer,
  CustomFormulaWrapper,
  LoaderWrapper,
  TabContent,
  TabsWrapper,
  Title,
} from 'src/pages/Settings/styled';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useAccount } from 'src/providers/account/hook/useAccount';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import { getIconForMetric } from 'src/utils';
import {
  DELETE_CUSTOM_FORMULA,
  UPDATE_COMPANY,
  CHANGE_PASSWORD,
} from 'src/utils/gql/mutations';
import { COMPANY, GET_CUSTOM_FORMULAS } from 'src/utils/gql/queries';
import {
  CustomFormula,
  CustomFormulaItemType,
  Mutation,
  MutationDeleteCustomFormulaArgs,
  MutationUpdateCompanyArgs,
  MutationChangePasswordArgs,
  Query,
} from 'src/utils/gql/types';
import PricingPlans from './PricingPlans';
import { EmailReportsSettings } from 'src/components/Settings/EmailReportsSettings';
import palette from 'src/theme/palette';

// Colori del tema Kipify
const kipifyGreen = '#88C81A';
const kipifyLightGreen = 'rgb(30, 237, 159)';
const kipifyError = '#EB484E';
const kipifyWarning = '#EFAB00';

// Component to show trial status
const TrialStatusCard: FC = () => {
  const {
    isInTrial,
    isTrialExpired,
    daysLeft,
    shouldFreeze,
    billingStatus,
    currentPlan,
    aiCallsUsed,
    aiCallsLimit,
    subscriptionEndDate,
    loading,
  } = useTrialStatus();

  if (loading) {
    return (
      <Card sx={{ mb: 3, bgcolor: '#f8fafc' }}>
        <CardContent>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Loading trial status...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = () => {
    if (shouldFreeze || isTrialExpired) return 'error';
    if (billingStatus === 'cancelled') return 'warning';
    if (daysLeft <= 2) return 'warning';
    return 'success';
  };

  const getStatusText = () => {
    if (shouldFreeze || isTrialExpired) return 'Trial Expired';
    if (billingStatus === 'cancelled') return 'Cancelled Subscription';
    if (isInTrial) return `Active Trial (${daysLeft} days left)`;
    return 'Active Subscription';
  };

  const aiCallsPercentage = (aiCallsUsed / aiCallsLimit) * 100;

  return (
    <Card sx={{ mb: 3, bgcolor: '#f8fafc' }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
            minWidth: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
            Account Status
          </Typography>
          <Chip
            label={getStatusText()}
            color={getStatusColor()}
            size="small"
            sx={{
              maxWidth: '100%',
              bgcolor:
                getStatusColor() === 'success' ? kipifyLightGreen : undefined,
              color: getStatusColor() === 'success' ? 'black' : undefined,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '&:hover': {
                bgcolor:
                  getStatusColor() === 'success' ? kipifyLightGreen : undefined,
                opacity: 0.9,
              },
            }}
          />
        </Box>

        {billingStatus === 'cancelled' && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              bgcolor: `${kipifyWarning}15`,
              border: `1px solid ${kipifyWarning}`,
              color: kipifyWarning,
              fontWeight: 500,
              '& .MuiAlert-icon': {
                color: kipifyWarning,
              },
            }}
          >
            Your subscription has been cancelled. You still have access until{' '}
            {subscriptionEndDate
              ? new Date(subscriptionEndDate).toLocaleDateString()
              : 'the end of your billing period'}
            . Note: Your data will not be updated from the day you uninstalled
            the app.
            <br />
            <br />
            <strong>
              To reactivate your subscription and resume data updates:
            </strong>
            <br />
            1. Disconnect your Shopify store from the Connectors page
            <br />
            2. Go to Shopify Admin and install the Kipify app again
            <br />
            3. You will be redirected back to Kipify
            <br />
            4. Connect your Shopify store again from the Connectors page
            <br />
            5. Complete the subscription payment on Shopify
            <br />
            6. You will be redirected back and your billing status will be
            automatically updated
            <br />
            <br />
            If you encounter any issues, please contact support.
          </Alert>
        )}

        {isInTrial && (
          <Alert
            severity={daysLeft <= 2 ? 'warning' : 'info'}
            sx={{
              mb: 2,
              bgcolor:
                daysLeft <= 2 ? `${kipifyWarning}15` : `${kipifyLightGreen}15`,
              border: `1px solid ${daysLeft <= 2 ? kipifyWarning : kipifyLightGreen}`,
              color: daysLeft <= 2 ? kipifyWarning : '#1a202c',
              fontWeight: 500,
              '& .MuiAlert-icon': {
                color: daysLeft <= 2 ? kipifyWarning : kipifyLightGreen,
              },
            }}
          >
            {daysLeft <= 2
              ? `Your trial expires in ${daysLeft} days. Upgrade your plan to continue.`
              : `You have ${daysLeft} days remaining in your free trial.`}
          </Alert>
        )}

        {shouldFreeze && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              bgcolor: `${kipifyError}15`,
              border: `1px solid ${kipifyError}`,
              color: kipifyError,
              '& .MuiAlert-icon': {
                color: kipifyError,
              },
            }}
          >
            Your trial has expired. Upgrade your plan to unlock all features.
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
            AI credits used: {aiCallsUsed.toLocaleString('en-US')} /{' '}
            {aiCallsLimit.toLocaleString('en-US')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(aiCallsPercentage, 100)}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#e5e7eb',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  aiCallsPercentage > 80
                    ? kipifyError
                    : aiCallsPercentage > 60
                      ? kipifyWarning
                      : kipifyLightGreen,
              },
            }}
          />
        </Box>

        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          Plan:{' '}
          {currentPlan === 'trial'
            ? 'Free Trial'
            : currentPlan?.charAt(0).toUpperCase() + currentPlan?.slice(1) ||
              'Unknown'}
        </Typography>

        {subscriptionEndDate && currentPlan !== 'trial' && (
          <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
            {billingStatus === 'cancelled' ? 'Expires' : 'Renewal'}:{' '}
            {new Date(subscriptionEndDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </Typography>
        )}

        {/* Upgrade button when AI credits are exhausted */}
        {aiCallsPercentage >= 100 && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                bgcolor: `${kipifyWarning}15`,
                border: `1px solid ${kipifyWarning}`,
                color: kipifyWarning,
                '& .MuiAlert-icon': {
                  color: kipifyWarning,
                },
              }}
            >
              You've reached your AI credit limit. Upgrade your plan to continue
              using AI features.
            </Alert>
            <Button
              variant="contained"
              sx={{
                bgcolor: kipifyLightGreen,
                color: 'black',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                '&:hover': {
                  bgcolor: 'rgb(25, 220, 140)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(30, 237, 159, 0.3)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
              onClick={() => {
                // Navigate to pricing tab
                const pricingTab = document.querySelector(
                  '[data-tab="pricing"]',
                );
                if (pricingTab) {
                  (pricingTab as HTMLElement).click();
                }
              }}
            >
              Upgrade Plan
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Mantiene il contenuto fuori dalla sidebar senza usare 100vw, che taglia il lato destro.
// Su mobile i maxWidth da tablet devono essere resettati: altrimenti resta calc(100%-200px)
// e la pagina occupa solo ~metà schermo (chip/form tagliati).
const SettingsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: palette.background.default,
  minHeight: '100vh',
  padding: '24px',
  marginLeft: '240px',
  width: 'calc(100% - 240px)',
  maxWidth: 'calc(100% - 240px)',
  boxSizing: 'border-box',
  overflow: 'visible',

  [theme.breakpoints.down('lg')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
    padding: '20px',
  },

  [theme.breakpoints.down('md')]: {
    marginLeft: '200px',
    width: 'calc(100% - 200px)',
    maxWidth: 'calc(100% - 200px)',
    padding: '16px',
  },

  // Mobile: sidebar overlay → full width (come Inventory/CustomReport)
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    maxWidth: '100%',
    padding: '12px',
    marginTop: '60px',
  },

  [theme.breakpoints.down('xs')]: {
    padding: '8px',
  },
}));

// Container interno completamente fluido e responsive
const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '0',
  boxSizing: 'border-box',
  overflow: 'visible', // Permette overflow per evitare tagli

  // Il contenuto si adatta automaticamente alla larghezza disponibile
}));

enum SettingTab {
  COMPANY,
  ACCOUNT,
  EMAIL_REPORTS,
  PRICING,
}

export const Settings: FC = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { user } = useAccount();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [value, setValue] = useState(SettingTab.COMPANY);
  const [open, setOpen] = useState(false);
  const [currentFormula, setcCurrentFormula] = useState<CustomFormula | null>(
    null,
  );

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const [updateCompany, { loading: updatingCompany }] = useMutation<
    Pick<Mutation, 'updateCompany'>,
    MutationUpdateCompanyArgs
  >(UPDATE_COMPANY, {
    onCompleted: ({ updateCompany }) => {
      companyformMethods.reset({
        name: updateCompany.name,
        industry: updateCompany.industry,
        companyDescription: updateCompany.companyDescription,
        usageOptions: updateCompany.usageOptions[0],
      });

      snackbar('Company has been successfully updated.', 'success');
    },
    onError,
  });

  const [changePassword, { loading: changingPassword }] = useMutation<
    Pick<Mutation, 'changePassword'>,
    MutationChangePasswordArgs
  >(CHANGE_PASSWORD, {
    onCompleted: () => {
      changePasswordFormMethods.reset();
      snackbar('Password has been successfully changed.', 'success');
    },
    onError,
  });

  const [deleteFormula, { loading: deletingFormula }] = useMutation<
    Pick<Mutation, 'deleteCustomFormula'>,
    MutationDeleteCustomFormulaArgs
  >(DELETE_CUSTOM_FORMULA, {
    refetchQueries: [{ query: GET_CUSTOM_FORMULAS }],
    onCompleted: () => {
      setOpen(false);
      snackbar(
        `${currentFormula?.name} has been successfully deleted.`,
        'success',
      );
    },
    onError,
  });

  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
  } = useQuery<Pick<Query, 'company'>, never>(COMPANY);
  const {
    data: customFormulasData,
    loading: formulasLoading,
    error: formulasError,
  } = useQuery<Pick<Query, 'getAllCustomFormulas'>, never>(GET_CUSTOM_FORMULAS);

  const companyformMethods = useForm<CompanyFormValues>({
    mode: 'onChange',
    resolver: yupResolver(companyFormSchema),
    criteriaMode: 'all',
    defaultValues: {
      name: '',
      industry: '',
      companyDescription: '',
    },
  });

  const changePasswordFormMethods = useForm<ChangePasswordFormValues>({
    mode: 'onChange',
    resolver: yupResolver(changePasswordFormSchema),
    criteriaMode: 'all',
    defaultValues: {
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      repeatPassword: '',
    },
  });

  // Force reset password fields to empty on component mount
  React.useEffect(() => {
    changePasswordFormMethods.setValue('currentPassword', '');
    changePasswordFormMethods.setValue('newPassword', '');
    changePasswordFormMethods.setValue('repeatPassword', '');
  }, []);

  const handleSubmit: SubmitHandler<CompanyFormValues> = (companyData) => {
    updateCompany({
      variables: {
        newCompanyData: {
          name: companyData.name,
          industry: companyData.industry,
          companyDescription: companyData.companyDescription,
          usageOptions: [companyData.usageOptions],
        },
      },
    });
  };

  const handleChangePasswordSubmit: SubmitHandler<ChangePasswordFormValues> = (
    passwordData,
  ) => {
    changePassword({
      variables: {
        input: {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
      },
    });
  };

  const handleDeleteFormula = () =>
    deleteFormula({ variables: { id: currentFormula?.id || '' } });
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newValue === SettingTab.COMPANY) {
        next.delete('tab');
      } else if (newValue === SettingTab.ACCOUNT) {
        next.set('tab', 'account');
      } else if (newValue === SettingTab.EMAIL_REPORTS) {
        next.set('tab', 'email-reports');
      } else {
        next.set('tab', 'pricing');
      }
      return next;
    });
  };
  const handleClose = () => setOpen(false);

  const handleDeleteClick = (customFormula: CustomFormula) => {
    setOpen(true);
    setcCurrentFormula(customFormula);
  };

  const handleEditClick = (customFormulaId: string) => {
    navigate(`${ROUTE_SOURCES.EDIT_FORMULA}/${customFormulaId}`);
  };

  const handleAdd = () => navigate(`${ROUTE_SOURCES.CREATE_FORMULA}`);

  useEffect(() => {
    if (companyData) {
      companyformMethods.reset({
        name: companyData.company.name,
        industry: companyData.company.industry,
        companyDescription: companyData.company.companyDescription,
        usageOptions: companyData.company.usageOptions[0],
      });
    }
  }, [companyData, companyformMethods]);

  useEffect(() => {
    if (user?.email) {
      changePasswordFormMethods.setValue('email', user.email);
    }
  }, [user?.email, changePasswordFormMethods]);

  // Handle company error
  useEffect(() => {
    if (companyError) {
      onError(companyError);
    }
  }, [companyError, onError]);

  // Handle formulas error
  useEffect(() => {
    if (formulasError) {
      onError(formulasError);
    }
  }, [formulasError, onError]);

  // Gestione tab automatico dal parametro URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'pricing') {
      setValue(SettingTab.PRICING);
    } else if (tabParam === 'account') {
      setValue(SettingTab.ACCOUNT);
    } else if (tabParam === 'email-reports') {
      setValue(SettingTab.EMAIL_REPORTS);
    } else if (tabParam === 'company') {
      setValue(SettingTab.COMPANY);
    } else {
      // Nessun `tab` (es. /settings): tab Company e niente "sticky" su Pricing
      setValue(SettingTab.COMPANY);
    }
  }, [searchParams]);

  return (
    <SettingsContainer>
      <ContentContainer>
        <Title variant="h3" sx={{ color: kipifyLightGreen }}>
          Settings
        </Title>
        <Box sx={{ width: '100%' }}>
          <TabsWrapper>
            <Tabs
              value={value}
              onChange={handleChange}
              variant={isMobile ? 'fullWidth' : 'standard'}
              scrollButtons={isTablet ? 'auto' : false}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: kipifyLightGreen,
                },
                '& .MuiTab-root.Mui-selected': {
                  color: kipifyLightGreen,
                },
              }}
            >
              <Tab label="Company" />
              <Tab label="Account" />
              <Tab label="Email Reports" data-tab="email-reports" />
              <Tab label="Pricing" data-tab="pricing" />
            </Tabs>
          </TabsWrapper>
          {value === SettingTab.COMPANY ? (
            <TabContent>
              <FormProvider {...companyformMethods}>
                <CompanyForm
                  onSubmit={handleSubmit}
                  isUpdateForm
                  buttonLoading={updatingCompany}
                  inputLoading={companyLoading}
                />
              </FormProvider>
              <CustomFormulaWrapper>
                <Typography variant="h5">Custom formula</Typography>
                <AddButton onClick={handleAdd}>
                  <AddBoxIcon />
                </AddButton>
              </CustomFormulaWrapper>
              {formulasLoading && (
                <LoaderWrapper>
                  <CircularProgress size={24} />
                </LoaderWrapper>
              )}
              {customFormulasData?.getAllCustomFormulas.map(
                (formula, index) => (
                  <FormulaCollapse
                    title={formula.name}
                    key={index}
                    onEdit={() => handleEditClick(formula.id)}
                    onDelete={() => handleDeleteClick(formula)}
                  >
                    {formula.formulaData.map((item, i) => (
                      <Chip
                        key={i}
                        variant="outlined"
                        label={
                          <ChipLabelContainer>
                            {getIconForMetric(item.connectorType)}
                            {item.type === CustomFormulaItemType.metric ? (
                              <Typography>{item.label}</Typography>
                            ) : (
                              <OperatorIcon operator={item.label} />
                            )}
                          </ChipLabelContainer>
                        }
                      />
                    ))}
                  </FormulaCollapse>
                ),
              )}
            </TabContent>
          ) : value === SettingTab.ACCOUNT ? (
            <TabContent>
              {/* Trial Status Card - fully responsive */}
              <TrialStatusCard />
              <FormProvider {...changePasswordFormMethods}>
                <ChangePasswordForm
                  onSubmit={handleChangePasswordSubmit}
                  loading={changingPassword}
                  userEmail={user?.email || ''}
                />
              </FormProvider>
            </TabContent>
          ) : value === SettingTab.EMAIL_REPORTS ? (
            <TabContent>
              <EmailReportsSettings />
            </TabContent>
          ) : (
            <TabContent>
              <PricingPlans />
            </TabContent>
          )}
        </Box>
      </ContentContainer>
      <DeleteModal
        open={open}
        onClose={handleClose}
        onDelete={handleDeleteFormula}
        name={currentFormula?.name || ''}
        title="Delete Custom formula?"
        loading={deletingFormula}
      />
    </SettingsContainer>
  );
};
